import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, GraduationCap, Megaphone, Coffee } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
};

const fmtMoney = (amountMinor, currency) => {
  const amount = (amountMinor || 0) / 100;
  return `${amount.toFixed(0)} ${(currency || "").toUpperCase()}`;
};

const STATUS_STYLE = {
  paid: "bg-[var(--tint-mint)] text-[var(--teal)]",
  pending: "bg-[var(--tint-butter)] text-[var(--sun)]",
  failed: "bg-red-50 text-red-500",
  expired: "bg-slate-100 text-slate-500",
  refunded: "bg-slate-100 text-slate-500",
};

const STATUS_LABEL = {
  paid: "plătit",
  pending: "în așteptare",
  failed: "eșuat",
  expired: "expirat",
  refunded: "returnat",
};

export const AdminPaymentsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/payments")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e, "Nu am putut încărca colaborările")))
      .finally(() => setLoading(false));
  }, []);

  const paidRows = rows.filter((r) => r.payment_status === "paid");
  const totalRON = paidRows
    .filter((r) => (r.currency || "").toLowerCase() === "ron")
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <main data-testid="admin-payments" className="mx-auto max-w-6xl px-6 py-12">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>

      <div className="mt-8 border-b border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Colaborări</p>
        <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Cumpără-mi o cafea</h1>
        <div className="mt-4 flex flex-wrap gap-3">
          <div className="rounded-2xl bg-[var(--tint-mint)] px-5 py-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--teal)]">Total încasat</p>
            <p className="mt-1 font-serif text-2xl text-[#F7F1E8]">{(totalRON / 100).toFixed(0)} RON</p>
          </div>
          <div className="rounded-2xl bg-[var(--tint-peach)] px-5 py-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--coral)]">Colaborări plătite</p>
            <p className="mt-1 font-serif text-2xl text-[#F7F1E8]">{paidRows.length}</p>
          </div>
          <div className="rounded-2xl bg-[var(--tint-butter)] px-5 py-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--sun)]">În așteptare</p>
            <p className="mt-1 font-serif text-2xl text-[#F7F1E8]">
              {rows.filter((r) => r.payment_status === "pending").length}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68" data-testid="payments-loading">
          Îți aduc colaborările&hellip;
        </p>
      ) : rows.length === 0 ? (
        <div className="mt-10 rounded-3xl bg-[var(--tint-pink)] p-10 text-center" data-testid="payments-empty">
          <Coffee className="mx-auto h-8 w-8 text-[var(--accent)]" strokeWidth={1.4} />
          <p className="mt-4 font-serif text-2xl text-[#F7F1E8]">Încă nimeni nu ți-a cumpărat o cafea.</p>
          <p className="mt-2 text-sm text-[#F7F1E8]/78">
            Dă mai departe pagina <Link to="/sponsorizeaza" className="underline">„Cumpără-mi o cafea"</Link> —
            cineva drag va apărea curând.
          </p>
        </div>
      ) : (
        <ul className="mt-8 divide-y divide-[color-mix(in_srgb,var(--milk)_18%,transparent)]" data-testid="payments-list">
          {rows.map((r) => {
            const Icon = r.service === "consultation" ? GraduationCap : Megaphone;
            const st = r.payment_status || "pending";
            return (
              <li key={r.session_id} data-testid={`payment-row-${r.session_id}`} className="grid grid-cols-1 gap-3 py-6 md:grid-cols-12">
                <div className="flex items-start gap-3 md:col-span-5">
                  <div className={`mt-1 rounded-full p-2 ${r.service === "consultation" ? "bg-[var(--tint-peach)]" : "bg-[var(--tint-butter)]"}`}>
                    <Icon className="h-4 w-4 text-[#F7F1E8]/86" strokeWidth={1.6} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-serif text-lg font-medium text-[#F7F1E8]">{r.buyer_name}</p>
                    <p className="text-xs text-[#F7F1E8]/68">
                      <a className="hover:text-[#F7F1E8]" href={`mailto:${r.buyer_email}`}>{r.buyer_email}</a>
                    </p>
                    {r.buyer_message && (
                      <p className="mt-2 line-clamp-3 whitespace-pre-line text-[13px] leading-relaxed text-[#F7F1E8]/86">
                        {r.buyer_message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#F7F1E8]/68">
                    {r.service === "consultation" ? "Discuție la o cafea" : "Sponsorizare"}
                  </p>
                  <p className="mt-1 text-sm text-[#F7F1E8]">{r.tier_label}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#F7F1E8]/68">Sumă</p>
                  <p className="mt-1 font-serif text-lg text-[#F7F1E8]">{fmtMoney(r.amount, r.currency)}</p>
                </div>
                <div className="md:col-span-2 md:text-right">
                  <span className={`inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLE[st] || STATUS_STYLE.pending}`}>
                    {STATUS_LABEL[st] || st}
                  </span>
                  <p className="mt-1 text-[11px] text-[#F7F1E8]/58">{fmtDate(r.created_at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
};

export default AdminPaymentsPage;
